import {useTheme, css} from "@emotion/react";
import { em, rem } from "@util/convertUnits";
import NavBar from "@/core/navigation/NavBar";

import useBreakpoints from "@util/breakpoints";
import {useState, useEffect, createRef} from 'react';
import useOutsideClick from "../helpers/useOutsideClick";
import useWindowResized from "../helpers/useWindowResized";
import {useHistory} from "react-router-dom";

import Logo from "@/base/Logo";
import { ButtonPill } from "@/base";
import StickyMainNav from "@/core/navigation/StickyMainNav";


const PageHeader = () => {

    const theme = useTheme();
    const history = useHistory();
    const [searchOpen, setSearchOpen] = useState(false);
    const searchSafeClickableParent = createRef();
    const searchBarInputRef = createRef();


    // The point where the `desktop nav` has enough space
    // and can be "switch on" from `mobile` to `desktop` styles
    //
    const navWideBP = theme.mobileNavBp;

    const headerCss = useBreakpoints([theme.breakpoint.sm, theme.breakpoint.ns, navWideBP, theme.breakpoint.lg, theme.breakpoint.nl])({
        position: ["relative", "relative", "relative", "relative"],
        top: "0",
        left: "0",
        zIndex: "5",
        width: "100%",
        height: ["10.0rem", "10.0rem", "10.0rem", "10.0rem"],
        padding: ["3rem 0", "", "", "3rem 0"], // ["2.2rem 1.6rem", "2.2rem 1.6rem", "2.2rem 3.2rem", "2.2rem 3.2rem", "2.2rem 4rem", "2.2rem 7.2rem"],
        boxShadow: '0px 1px 10px rgba(0, 0, 0, 0.05)'
    });

    const headerGradientBG = useBreakpoints([navWideBP])({
        position: "absolute",
        left: "0",
        top: "0",
        zIndex: "-1",
        display: ["none", "block"],
        width: "100%",
        height: rem(84),
        background: "#fff",
        // backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0.7) 0, rgba(255,255,255,0.7) 30%, rgba(255,255,255, 1%) 100%)",
        pointerEvents: "none",
    });

    const headerRowCss = useBreakpoints([theme.mobileNavBp])({
        position: "relative",
        display: "flex",
        padding: ["0 1.6rem",  "0 4rem"],
    });

    const navWrapCss = useBreakpoints([theme.mobileNavBp, theme.breakpoint.lg])({
        flexGrow: "1",
        flexWrap: "wrap",
        display: ["block", "flex"],
    });


    useEffect(() => {
        if (searchOpen) {
            searchBarInputRef.current.focus()
        }
    }, [searchOpen, searchBarInputRef])

    useOutsideClick(searchSafeClickableParent, () => {
        if (searchOpen) setSearchOpen(false);
    })

    useWindowResized(() => {
        if (searchOpen && window.innerWidth <= navWideBP) setSearchOpen(false);
    })

    useEffect(()=>{
        window.addEventListener('scroll', ()=>setSearchOpen(false));
        return ()=>window.removeEventListener('scroll', ()=>setSearchOpen(false));
    }, [])

    const triggerSearch = () => {
        const url = `/search/${searchBarInputRef.current.value}`
        searchBarInputRef.current.value = "";
        setSearchOpen(false);
        history.push(url);
    }

    const handleSearchKeyPress = (e) => {
        if (e.charCode == 13) triggerSearch()
    }

    const utilsCss = css(`
        display: none;
        @media (min-width: ${em(navWideBP, 16)}) {
        display: flex;
        flex-direction: column;
        position: relative;
        top: -11px;
        }
    `);

    const utilsInnerWrapperCss = css(`
        display: none;
        @media (min-width: ${em(navWideBP, 16)}) {
        display: flex;
        flex-direction: row;
        }
    `);

    const utilCtaCss = css(`
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 0 0 0.8rem;
    `);

    const callUs = css(`
        padding-top: 6px;
    `);

    const callUsNumber = css(`
        font-weight: bold;
    `);

    const searchCss = css(`
    position: relative;
    width: 4rem;
    height: 4rem;

    &:hover {
      .search_input {
        display: block; // TODO: create click to add/show input
      }
    }
  `);

    const searchIconCSS = css(`
    position: relative;
    display: block;
    width: 4rem;
    height: 4rem;
    padding: 0.8rem;
    border:0;
    outline: none !important;
    background-image: url(/images/common-icons/icon-search-black.svg); // TODO: detect light/dark display (white svg available)
    background-repeat: no-repeat;
    background-position: center center;
    background-color: transparent;
    &.open {
        background-image: url(/images/nav-icons/icon-close.svg); // TODO: This should probably be a dedicated icon? is it ok to share this?
    }
  `);

    const searchBarCss = css(`
        display:none;
        width: 100%;
        margin-top: 1em;
        border: 0;
        padding: 10px;
        &.open {
          display:block;
        }
    `)

    const searchBarWrapperCss = css(`
        position:relative;
        @media (min-width: ${theme.mobileNavBp}px) {
            padding: 2rem 4rem;
            background: #fff;
            margin-top: 2px;
            input{
                margin-top: 0px;
                border: 1px solid rgba(0,0,0,0.5);
                border-radius: 20px;
                overflow: hidden;
            }
        }
    `)

    const openedSearchIconCss = css(`
        background-image: url(/images/common-icons/icon-search-black.svg);
        background-repeat: no-repeat;
        background-position: center center;
        background-color: transparent;
        position: absolute;
        display: none;
        width: 4rem;
        height: 4rem;
        border:0;
        outline: none !important;
        right: 5rem;
        top: 0;
        bottom: 0;
        margin: auto;
        &.open {
          display:block;
        }
    `)

    return (
        <StickyMainNav>
            <div className={'header'} css={headerCss} ref={searchSafeClickableParent}>
                <div className="header_row maxW-100" css={headerRowCss}>
                    <Logo
                        src="/images/common-icons/LaCantina_Logo.svg"
                        alt="LaCantina Doors"
                    />
                    <div className="header_nav" css={navWrapCss}>
                        <NavBar bp={navWideBP}/>
                        <div className="header_util" css={utilsCss}>
                            <div className="header_util-inner-wrapper" css={utilsInnerWrapperCss}>
                                <div className="search" css={searchCss}>
                                    <button className={searchOpen ? "open search_icon" : "search_icon"}
                                            css={searchIconCSS}
                                            onClick={() => {
                                                setSearchOpen(!searchOpen);
                                            }}></button>
                                </div>
                                <div className="util_links" css={utilCtaCss}>
                                    <ButtonPill to={'/request-quote'} buttonTheme={'dark-rev'}>Request Quote</ButtonPill>
                                    <div className="callUs" css={callUs}>Call us <a href="tel:1-888-221-0141" className="callUsNumber" css={callUsNumber}>888.221.0141</a></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {searchOpen && (
                    <div css={searchBarWrapperCss}>
                        <div>
                            <input ref={searchBarInputRef} type={"text"} css={searchBarCss}
                                   className={searchOpen ? "open" : ""}
                                   onKeyPress={handleSearchKeyPress}/>
                        </div>
                        <button css={openedSearchIconCss} className={searchOpen ? "open" : ""}
                                onClick={triggerSearch}></button>
                    </div>
                )}
                <div css={headerGradientBG}></div>
            </div>

        </StickyMainNav>

    );
};

export default PageHeader;
